import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from "react-router-dom";

import {
  Alert, Box, Button, Checkbox, Divider, FormControlLabel, FormGroup,
  LinearProgress, Modal, Step, Stepper, StepLabel, TextField, Typography
} from '@mui/material';

import LocataireForm from './LocataireForm';
import GarantForm from './GarantForm';
import { initLocataireFields, initGarantFields } from 'utils/formFields';
import { fetchWithAuth } from 'utils/fetchWithAuth'
import usePersonSearch from 'hooks/usePersonSearch';

function EntreeLocataireModal({ lot, sciId, open, onClose }) {

  const [step, setStep] = useState(1);
  
  const steps = [
    "Locataire Principal",
    "Second Locataire",
    "Garant",
    "Informations Bail",
    "Annexes",
    "Résumé"
  ];

  const API_URL = process.env.REACT_APP_API_URL;
  const mainLocataire = usePersonSearch({
    API_URL,
    endpoint: "/api/locataires/autocomplete/",
    open,
    lot,
    initFields: initLocataireFields,
  });
  const secondLocataire = usePersonSearch({
    API_URL,
    endpoint: "/api/locataires/autocomplete/",
    open,
    lot,
    initFields: initLocataireFields,
  });
  const [bail, setBail] = useState({
    date_entree: '',
    loyer_nu: '',
    charges: '',
    loyer: '',
    caution: '',
    conditions_particulieres: '',
    annexes: {
      reglement_immeuble: false,
      ddt: false,
      etat_des_lieux: false,
      inventaire_mobilier: false,
    }
  });
  const Garant = usePersonSearch({
    API_URL,
    endpoint: "/api/garants/autocomplete/", 
    open,
    lot,
    initFields: initGarantFields,
  });
  const [showStepper, setShowStepper] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && lot) {
      setStep(1);
    }
  }, [open, lot]);

  const handleBailField = (field, value) => {
    setBail(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const toggleAnnexe = (key) => {
    setBail(prev => ({
      ...prev,
      annexes: {
        ...prev.annexes,
        [key]: !prev.annexes[key]
      }
    }));
  };
  const handleField = setFields => e => {
    const { name, value } = e.target;
    setFields(f => ({ ...f, [name]: value }));
  };
  const handleMainLocataireField = handleField(mainLocataire.setFields);
  const handleSecondLocataireField = handleField(secondLocataire.setFields);
  const handleGarantField = handleField(Garant.setFields);

  const handleSelectAny = (value, setFields, setIsExisting, initFields) => {
    if (value) {
      setFields(f => {
        const res = {};
        for (const key of Object.keys(f)) {
          res[key] = value[key] ?? "";
        }
        return res;
      });
      setIsExisting(true);
    } else {
      setFields(initFields());
      setIsExisting(false);
    }
  };
  const handleSelectMainLocataire = (value) =>
    handleSelectAny(value, mainLocataire.setFields, mainLocataire.setIsExisting, initLocataireFields);  
  const handleSelectSecondLocataire = (value) =>
    handleSelectAny(value, secondLocataire.setFields, secondLocataire.setIsExisting, initLocataireFields);
  const handleSelectGarant = value =>
    handleSelectAny(value, Garant.setFields, Garant.setIsExisting, initGarantFields);

  // Form validators
  const isValid = (fields, requiredKeys) =>
    requiredKeys.every(key => fields[key] && fields[key].trim() !== "");

  const garantRequiredFields = useMemo(() => ([
    "nom", "prenom", "email", "telephone", "adresse", "code_postal", "ville"
  ]), []);
  const locataireRequiredFields = useMemo(() => ([
    "nom", "prenom", "email", "telephone"
  ]), []);

  const generateBail = useCallback(async (action) => {
    setGenerating(true);

    const dataToSend = {
      action,
      locataire: mainLocataire.fields,
      lot_id: lot.id,
      loyer_nu: bail.loyer_nu,
      charges: bail.charges,
      loyer: bail.loyer,
      caution: bail.caution,
      annexes: bail.annexes,
      conditions_particulieres: bail.conditions_particulieres,
      date_debut: bail.date_entree,
    };

    if (isValid(secondLocataire.fields, locataireRequiredFields)) {
      dataToSend.second_locataire = secondLocataire.fields;
    }

    if (isValid(Garant.fields, garantRequiredFields)) {
      dataToSend.garant = Garant.fields;
    }

    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/generer-contrat-location/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dataToSend),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur lors de la génération");
      }

      if (action === "preview") {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        return;
      }

      if (action === "valider") {
        const json = await res.json();
        return json;
      }

    } finally {
      setGenerating(false);
    }
  }, [
    mainLocataire.fields,
    secondLocataire.fields,
    Garant.fields,
    locataireRequiredFields,
    garantRequiredFields,
    lot.id,
    bail.loyer_nu,
    bail.charges,
    bail.loyer,
    bail.caution,
    bail.annexes,
    bail.conditions_particulieres,
    bail.date_entree,
    API_URL,
  ]);

  useEffect(() => {
    if (step === 6 && !pdfUrl && !generating && validationStatus === null) {
      generateBail("preview");
    }

    if (step !== 6 && pdfUrl) {
      setPdfUrl(null);
    }

  }, [step, pdfUrl, generating, validationStatus, generateBail]);

  useEffect(() => {
    if (step === 7 && !generating && validationStatus === null) {
      setShowStepper(false);

      (async () => {
        const result = await generateBail("valider");

        if (!result) {
          setValidationStatus("ko");
          return;
        }

        setValidationStatus("ok");
        setTimeout(() => {
          onClose();
        }, 2500);
      })();
    }
  }, [step, generating, validationStatus, generateBail, onClose]);


  if (!lot || !lot.bien) return null; 

  return (
    
    <Modal open={!!open} onClose={onClose}>
      <Box sx={{
        p: 3, background: 'white', borderRadius: 2, minWidth: 500,
        maxWidth: 700, mx: 'auto', my: '10vh', boxShadow: 8,
        outline: 0, position: 'relative'
      }}>
        <Box sx={{ maxWidth: '100%', mb: 4 }}>
          {showStepper && (
            <Stepper activeStep={step - 1} alternativeLabel sx={{ mb: 4 }}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </Box>

        <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
          Bail pour le lot : {lot.nom}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {`${lot.bien.adresse}, ${lot.bien.code_postal} ${lot.bien.ville}`}
        </Typography>

        <Typography variant="h6" sx={{ mb: 2 }}>
          {{
            1: "Locataire principal",
            2: "Ajouter un second locataire (optionnel)",
            3: "Ajouter un garant (optionnel)",
            4: "Informations du bail",
            5: "Annexes et autres conditions particulières",
            6: "Résumé du bail"
          }[step]}
        </Typography>

        <Box sx={{ maxWidth: 480, mx: 'auto' }}>      
          {step === 1 && (
            <LocataireForm
              autocompleteOptions={mainLocataire.results}
              searchValue={mainLocataire.search}
              onSearchChange={mainLocataire.setSearch}
              loading={mainLocataire.loading}
              onAutocompleteChange={handleSelectMainLocataire}
              onFieldChange={handleMainLocataireField}
              fields={mainLocataire.fields}
              isExisting={mainLocataire.isExisting}
            />
          )}

          {step === 2 && (
            <LocataireForm
              autocompleteOptions={secondLocataire.results}
              searchValue={secondLocataire.search}
              onSearchChange={secondLocataire.setSearch}
              loading={secondLocataire.loading}
              onAutocompleteChange={handleSelectSecondLocataire}
              onFieldChange={handleSecondLocataireField}
              fields={secondLocataire.fields}
              isExisting={secondLocataire.isExisting}
            />
          )}

          {step === 3 && (
            <GarantForm
              autocompleteOptions={Garant.results}
              searchValue={Garant.search}
              onSearchChange={Garant.setSearch}
              loading={Garant.loading}
              onAutocompleteChange={handleSelectGarant}
              onFieldChange={handleGarantField}
              fields={Garant.fields}
              isExisting={Garant.isExisting}
            />
          )}

          {step === 4 && (
            <>
              <TextField
                label='Date entrée'
                type='date'
                value={bail.date_entree}
                onChange={e => handleBailField('date_entree', e.target.value)}
                required
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              {!lot.bien.meuble ? (
                <>
                  <TextField
                    label='Loyer nu (€)'
                    type='number'
                    value={bail.loyer_nu}
                    onChange={e => handleBailField('loyer_nu', e.target.value)}
                    required
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label='Provision de charges (€)'
                    type='number'
                    value={bail.charges}
                    onChange={e => handleBailField('charges', e.target.value)}
                    required
                    fullWidth
                    margin="normal"
                  />
                </>
              ) : (
                <TextField
                  label='Loyer toutes charges comprises (€)'
                  type='number'
                  value={bail.loyer}
                  onChange={e => handleBailField('loyer', e.target.value)}
                  required
                  fullWidth
                  margin="normal"
                />
              )}
              <TextField
                label='Caution (€)'
                type='number'
                value={bail.caution}
                onChange={e => handleBailField('caution', e.target.value)}
                required
                fullWidth
                margin="normal"
              />
            </>
          )}
          {step === 5 && (
            <>
              <TextField
                label="Conditions particulières"
                multiline
                minRows={6}
                maxRows={12}
                value={bail.conditions_particulieres}
                onChange={e => handleBailField("conditions_particulieres", e.target.value)}
                fullWidth
                margin="normal"
                placeholder="Exemples :&#10;- Le locataire peut installer un lave-vaisselle&#10;- Pas d’animaux bruyants&#10;- Accès au jardin autorisé le week-end"
                InputLabelProps={{ shrink: true }}
              />
              <Divider sx={{ my: 3 }}>Annexes</Divider>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bail.annexes.reglement_immeuble}
                        onChange={() => toggleAnnexe("reglement_immeuble")}
                      />
                    }
                    label="Extrait du règlement de l'immeuble"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bail.annexes.ddt}
                        onChange={() => toggleAnnexe("ddt")}
                      />
                    }
                    label="Dossier de diagnostic technique"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bail.annexes.etat_des_lieux}
                        onChange={() => toggleAnnexe("etat_des_lieux")}
                      />
                    }
                    label="État des lieux"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bail.annexes.inventaire_mobilier}
                        onChange={() => toggleAnnexe("inventaire_mobilier")}
                      />
                    }
                    label="Inventaire du mobilier"
                  />
                </FormGroup>
            </>
          )}
          {step === 6 && (
            <>

              {generating && <LinearProgress sx={{ mb: 2 }} />}
              {pdfUrl && (
                <Box sx={{ mt: 2 }}>
                  <iframe
                    src={pdfUrl}
                    width="100%"
                    height="400"
                    title="Prévisualisation PDF"
                    style={{ border: "1px solid #ddd", borderRadius: 4 }}
                  />
                  <Button
                    component="a"
                    href={pdfUrl}
                    download="projet_bail.pdf"
                    target="_blank"
                    sx={{ mt: 2 }}
                    color="success"
                  >
                    Télécharger le PDF
                  </Button>
                </Box>
              )}
            </>
          )}
          {step === 7 && (
            <>
              {generating && <LinearProgress sx={{ mb: 2 }} />}

              {validationStatus === "ok" && (
                <Alert severity="success">
                  Bail validé et enregistré
                </Alert>
              )}
              {validationStatus === "ko" && (
                <Alert severity="error">
                  Erreur lors de la validation du bail
                </Alert>
              )}

              <Button
                onClick={() => {
                  navigate(`/sci/${sciId}`);
                  navigate(0);
                }} 
                sx={{ mt: 2 }}
              >
                Fermer
              </Button>
            </>
          )}
        </Box>

        {/* ZONE DES BOUTONS BAS DE PAGE */}
        {step < 7 && (
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button onClick={onClose}>Annuler</Button>
            <Box sx={{ flex: 1 }} />
            {(step === 2 || step === 3) && (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 2 && secondLocataire.isExisting) ||
                  (step === 3 && Garant.isExisting)   
                }
              >
                Ignorer
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={
                (step === 1 && !isValid(mainLocataire.fields, locataireRequiredFields))
                || (step === 2 && !isValid(secondLocataire.fields, locataireRequiredFields))
                || (step === 3 && !isValid(Garant.fields, garantRequiredFields))
                || (step === 4 && (
                    !bail.date_entree ||
                    !bail.caution ||
                    (lot.bien.meuble ? !bail.loyer : (!bail.loyer_nu || !bail.charges))
                ))
              }
              onClick={() => setStep(step + 1)}
            >
              Suivant
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default EntreeLocataireModal;